import { InjectionKey, provide, inject, Ref } from "vue";
import { assign, createMachine, InterpreterFrom, send } from "xstate-4";
import { useInterpret, useActor } from "@xstate-4/vue";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";
import {
  DTOActionRequiredNotification,
  DTOActionRequiredNotificationType,
  DTOInfoNotification,
  DTOInfoNotificationType,
} from "vue-app/api/dto";
import { useRouter } from "vue-app/composables/useRouter";
import { handleClickSendVerifyEmail } from "vue-app/api/menu/account.ts";
import { usePackageManager } from "vue-app/composables/usePackageManager";

export type Context = {
  notifications: null | DTOInfoNotification[];
  actions: null | DTOActionRequiredNotification[];
  open: boolean;
  currentTab: "actions" | "notifications";
  errors: string[];
  newNotifications: number;
  newActions: number;
};

export const machineNotificationsMenu = createMachine(
  {
    id: "NotificationsMenu",
    tsTypes: {} as import("./machineNotificationsMenu.typegen").Typegen0,
    schema: {
      events: {} as
        | { type: "FETCH_NOTIFICATIONS" }
        | { type: "MARK_AS_READ" }
        | { type: "OPEN" }
        | { type: "CLOSE" }
        | { type: "SWITCH_TAB"; tab: "actions" | "notifications" }
        | {
            type: "REDIRECT_FOR_ACTION";
            action: DTOActionRequiredNotificationType | DTOInfoNotificationType;
            details: { [index: string]: unknown };
          },
      context: {} as Context,
      services: {} as {
        fetchNotifications: {
          data: DTOInfoNotification[];
        };
        fetchActions: {
          data: DTOActionRequiredNotification[];
        };
      },
    },
    context: {
      notifications: null,
      actions: null,
      open: false,
      currentTab: "actions",
      errors: [],
      newNotifications: 0,
      newActions: 0,
    },
    type: "parallel",
    states: {
      fetching: {
        initial: "fetchingData",
        states: {
          fetchingData: {
            tags: "fetchingData",
            entry: "resetErrors",
            type: "parallel",
            states: {
              fetchingNotifications: {
                initial: "pending",
                states: {
                  pending: {
                    invoke: {
                      src: "fetchNotifications",
                      id: "fetchNotifications",
                      onDone: [
                        {
                          target: "success",
                          actions: "assignNotifications",
                        },
                      ],
                      onError: [
                        {
                          target: "success",
                          actions: "assignErrors",
                        },
                      ],
                    },
                  },
                  success: {
                    type: "final",
                  },
                },
              },
              fetchingActions: {
                initial: "pending",
                states: {
                  pending: {
                    invoke: {
                      src: "fetchActions",
                      id: "fetchActions",
                      onDone: [
                        {
                          target: "success",
                          actions: "assignActions",
                        },
                      ],
                      onError: [
                        {
                          target: "success",
                          actions: "assignErrors",
                        },
                      ],
                    },
                  },
                  success: {
                    type: "final",
                  },
                },
              },
            },
            onDone: {
              target: "waiting",
              actions: ["assignNewNotifications", "assignNewActions", "assignCurrentTab"],
            },
          },
          waiting: {
            tags: "waiting",
            after: {
              300000: {
                // 5 * 60 * 1000 = 5 minutes
                target: "fetchingData",
              },
            },
            on: {
              FETCH_NOTIFICATIONS: {
                target: "fetchingData",
              },
              MARK_AS_READ: {
                actions: ["resetNewNotifications", "markAllNotificationsAsRead", "updateNotificationsStatusInDatabase"],
              },
            },
          },
        },
      },
      idle: {
        tags: "idle",
        initial: "closed",
        states: {
          closed: {
            tags: "closed",
            on: {
              OPEN: {
                target: "open",
              },
            },
          },
          open: {
            tags: "open",
            on: {
              CLOSE: {
                target: "closed",
              },
              SWITCH_TAB: {
                actions: "assignCurrentTab",
              },
              REDIRECT_FOR_ACTION: {
                actions: ["goToActionPage", send("CLOSE")],
              },
            },
          },
        },
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    guards: {
      //
    },
    actions: {
      assignNotifications: assign({
        notifications: (context, event) => {
          xlog({ label: "assignNotifications", type: "action", context, event });
          return event.data;
        },
      }),
      assignActions: assign({
        actions: (context, event) => {
          xlog({ label: "assignActions", type: "action", context, event });
          return event.data;
        },
      }),
      assignErrors: assign({
        errors: (context, event) => {
          xlog({ label: "assignErrors", type: "action", context, event });
          if (event.type === "error.platform.fetchActions") {
            return [...context.errors, "actions"];
          }
          if (event.type === "error.platform.fetchNotifications") {
            return [...context.errors, "notifications"];
          }
          return context.errors;
        },
      }),
      resetErrors: assign({
        errors: (context, event) => {
          xlog({ label: "resetErrors", type: "action", context, event });
          return [];
        },
      }),
      resetNewNotifications: assign({
        newNotifications: (context, event) => {
          xlog({ label: "newNotifications", type: "action", context, event });
          return 0;
        },
      }),
      updateNotificationsStatusInDatabase: (context, event) => {
        xlog({ label: "updateNotificationsStatusInDatabase", type: "action", context, event });
        axios.post("/notification/mark-as-read");
      },
      markAllNotificationsAsRead: assign({
        notifications: (context, event) => {
          xlog({ label: "markAllNotificationsAsRead", type: "action", context, event });
          return (
            context.notifications?.map((notification) => ({
              ...notification,
              read: true,
            })) || []
          );
        },
      }),
      assignNewNotifications: assign({
        newNotifications: (context, event) => {
          xlog({ label: "assignNewNotifications", type: "action", context, event });
          return context.notifications?.filter(({ read }) => !read).length || 0;
        },
      }),
      assignNewActions: assign({
        newActions: (context, event) => {
          xlog({ label: "assignNewActions", type: "action", context, event });
          return context.actions?.length || 0;
        },
      }),
      assignCurrentTab: assign({
        currentTab: (context, event) => {
          xlog({ label: "assignCurrentTab", type: "action", context, event });
          if (event.type === "SWITCH_TAB") {
            return event.tab;
          }
          return context.newActions > 0 || context.errors.includes("actions") ? "actions" : "notifications";
        },
      }),
      goToActionPage: (context, event) => {
        xlog({ label: "goToActionPage", type: "action", context, event });
        const router = useRouter();
        const { openSMS } = usePackageManager();
        switch (event.action) {
          case "STORE_NOT_INTEGRATED":
            router?.push("/app/integrations");
            break;
          case "STRIPE_UNPAID_INVOICE_LOW":
          case "STRIPE_UNPAID_INVOICE_MEDIUM":
          case "STRIPE_UNPAID_INVOICE_HIGH":
          case "STRIPE_NO_VALID_CARD_CONNECTED":
          case "STRIPE_NO_PAYMENT_METHOD":
            router?.push({ name: "PageAccountBilling" });
            break;
          case "AGENT_EMAIL_NOT_VERIFIED":
            handleClickSendVerifyEmail();
            break;
          case "ENGAGEMENT_PAUSED_NO_COUPON_AVAILABLE":
            router?.push("/app/custom-flows/coupons");
            break;
          case "FEED_NO_VALID_FEED_CONFIGURATION":
            router?.push("/app/products");
            break;
          case "BILLING_PAYMENT_TIER_CHANGED":
            router?.push({ name: "PageAccountBilling" });
            break;
          case "NEWSLETTER_NOT_SENT":
            router?.push("/app/newsletter-hub");
            break;
          case "SMS_CREDITS_LOW":
            openSMS();
            break;
          case "PAYMENTS_PROVIDER_NOT_FOUND":
          default:
            window.showToast(window._trans._notifications.types.default.action, "info");
        }
      },
    },
    services: {
      fetchNotifications: async (context, event) => {
        xlog({ label: "fetchNotifications", context, event });
        const response = await axios.get("/notification/info");
        return response.data;
      },
      fetchActions: async (context, event) => {
        xlog({ label: "fetchActions", context, event });
        const response = await axios.get("/notification/action-required");
        return response.data;
      },
    },
  }
);
export type NotificationsMenuActor = InterpreterFrom<typeof machineNotificationsMenu>;
const notificationsMenuServiceSymbol: InjectionKey<NotificationsMenuActor> = Symbol();
export function provideNotificationsMenuActor(context?: Partial<Context>): {
  send: NotificationsMenuActor["send"];
  state: Ref<NotificationsMenuActor["state"]>;
} {
  // @ts-ignore
  const actor = useInterpret(machineNotificationsMenu, {
    devTools: true,
    context: { ...machineNotificationsMenu.context, ...context },
  });
  // @ts-ignore
  provide(notificationsMenuServiceSymbol, actor);
  // @ts-ignore
  return useActor(actor);
}
export function useNotificationsMenuActor(): {
  send: NotificationsMenuActor["send"];
  state: Ref<NotificationsMenuActor["state"]>;
} {
  const actor = inject(notificationsMenuServiceSymbol);
  // @ts-ignore
  return useActor(actor);
}
